import {
  ORDER_STATUS_LABELS,
  LEAD_STATUS_LABELS,
  type OrderStatus,
  type LeadStatus,
} from "@ckgo2/shared/types";

// Presentation layer for order/lead statuses. Enum values + human labels live in
// @ckgo2/shared (consumed by API + web too); the Tailwind classes are admin-only,
// so they stay here. Single source for every order/lead badge + kanban board.

export interface StatusBadge {
  label: string;
  className: string;
}

export interface KanbanColumn<S extends string> {
  id: S;
  label: string;
  color: string; // dot / accent
  bgColor: string; // column background
}

// ── Orders ────────────────────────────────────────────────────────────────
const ORDER_BADGE_CLASSNAMES: Record<OrderStatus, string> = {
  inquiry: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  quoted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  confirmed: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  deposit_paid: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  fully_paid: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  completed: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  cancelled: "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400",
};

export const ORDER_STATUS_CONFIG: Record<OrderStatus, StatusBadge> = Object.fromEntries(
  (Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((s) => [
    s,
    { label: ORDER_STATUS_LABELS[s], className: ORDER_BADGE_CLASSNAMES[s] },
  ]),
) as Record<OrderStatus, StatusBadge>;

// Board columns = the active pipeline (cancelled is a terminal side-state, not a column).
export const ORDER_KANBAN_COLUMNS: KanbanColumn<OrderStatus>[] = [
  {
    id: "inquiry",
    label: "Inquiries",
    color: "bg-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
  },
  {
    id: "quoted",
    label: "Quoted",
    color: "bg-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  {
    id: "confirmed",
    label: "Confirmed",
    color: "bg-purple-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/10",
  },
  {
    id: "deposit_paid",
    label: "Deposit paid",
    color: "bg-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/10",
  },
  {
    id: "fully_paid",
    label: "Fully paid",
    color: "bg-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/10",
  },
  {
    id: "completed",
    label: "Completed",
    color: "bg-emerald-500",
    bgColor: "bg-emerald-50 dark:bg-emerald-900/10",
  },
];

// ── Leads ─────────────────────────────────────────────────────────────────
const LEAD_BADGE_CLASSNAMES: Record<LeadStatus, string> = {
  received: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  quoted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  call_back: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400",
  dormant: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  sold: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400",
  lost: "bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400",
};

export const LEAD_STATUS_CONFIG: Record<LeadStatus, StatusBadge> = Object.fromEntries(
  (Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((s) => [
    s,
    { label: LEAD_STATUS_LABELS[s], className: LEAD_BADGE_CLASSNAMES[s] },
  ]),
) as Record<LeadStatus, StatusBadge>;

// Lead board = the funnel. `lost` is terminal (shown as a count, not a column).
export const LEAD_KANBAN_COLUMNS: KanbanColumn<LeadStatus>[] = [
  {
    id: "received",
    label: "Received",
    color: "bg-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
  },
  {
    id: "quoted",
    label: "Quoted",
    color: "bg-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  {
    id: "call_back",
    label: "Call back",
    color: "bg-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/10",
  },
  {
    id: "dormant",
    label: "Dormant",
    color: "bg-slate-500",
    bgColor: "bg-slate-50 dark:bg-slate-800/50",
  },
  { id: "sold", label: "Sold", color: "bg-green-500", bgColor: "bg-green-50 dark:bg-green-900/10" },
];
